/**
 * הקמת חברה — onboarding, within purchased capacity. OA-0001 §3.11, 02 §5.2, D2.
 *
 * THREE FACTS THIS FORM IS BUILT AROUND:
 *
 *   · A NEW COMPANY STARTS `prospect`, NOT `active`. D2's billable count reads `lifecycle_status`,
 *     so creating a row must not silently create a charge. The form says so, because an office that
 *     expects a new company to be immediately workable will otherwise report it as a bug.
 *   · CAPACITY IS CHECKED BEFORE CREATION, by the server. If the office is at its limit the refusal
 *     carries the count, the limit and a route — and it appears here, before the form, rather than
 *     after the office has typed a legal name.
 *   · `entityKind` IS DECLARED, NEVER SNIFFED. 02 §5.2's six kinds are a choice a person makes;
 *     an עוסק מורשה and a חברה בע״מ are not distinguishable from a name, and guessing one is how a
 *     company acquires a legal form nobody chose.
 *
 * The kind is offered as radio buttons and not as a dropdown, deliberately: 09 §3.4 puts every
 * dropdown control in the context bar, and `shell/contextSingleSource.test.ts` enforces that against
 * the source tree so a module can never grow its own picker. (That test scans for the tag name
 * without stripping comments, which is why this paragraph describes the rule instead of quoting it —
 * a rule that cannot be written down beside the code it governs is a rule that gets forgotten, so it
 * is written down, in words the scanner does not mistake for a violation.)
 */

import { useState } from 'react';
import type { TextKey } from '../i18n/catalogue';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { CapacityRefusalNotice } from '../components/CapacityPanel';
import { DenialNotice } from '../components/DenialNotice';
import { ErrorState, StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ApiError, ConflictError, DeniedError } from '../api/client';
import type { ConflictBody } from '../api/client';
import { callOperation } from '../api/operations';
import type { DenyBody } from '../api/contract';
import { fixtureCompanyCapacityRefusal, fixturesEnabled } from '../api/fixtures';
import { companyRefusalText } from '../api/companyText';
import { routes } from '../routes';


/** 02 §5.2 — the six entity kinds, verbatim, in office Hebrew. */
const ENTITY_KINDS: readonly { readonly code: string; readonly label: TextKey }[] = [
  { code: 'company_ltd', label: 'entity.ltd' },
  { code: 'licensed_dealer', label: 'entity.licensed' },
  { code: 'exempt_dealer', label: 'entity.exempt' },
  { code: 'individual', label: 'entity.individual' },
  { code: 'ngo', label: 'entity.association' },
  { code: 'reporting_entity', label: 'entity.reporting' },
];

/**
 * WHICH NUMBER THIS ENTITY HAS, in its own name.
 *
 * `core.legal_entity.tax_id_kind` is a different vocabulary from `entity_kind` — 0002 declares both
 * — and an operator is owed the name their own documents use: a company has a ח״פ, a dealer has a
 * מספר עוסק, a person has a תעודת זהות. One label for all three would be the generic *"מספר מזהה
 * (ח.פ / ע.מ)"* this screen used to show, which names two of six.
 */
function officialIdLabelHe(entityKind: string): string {
  const { t } = useLocale();

  switch (entityKind) {
    case 'company_ltd':
    case 'reporting_entity':
      return t('entity.numberLtd');
    case 'licensed_dealer':
    case 'exempt_dealer':
      return t('entity.numberDealer');
    case 'individual':
      return t('entity.numberId');
    case 'ngo':
      return t('entity.numberAssociation');
    default:
      return t('entity.number');
  }
}

type Outcome =
  | { readonly kind: 'created'; readonly companyUid: string }
  | { readonly kind: 'denied'; readonly body: DenyBody }
  /** The gate allowed it and the write could not happen, with the server's own reason. */
  | { readonly kind: 'refused'; readonly body: ConflictBody; readonly requestUid: string }
  | { readonly kind: 'error'; readonly requestUid: string }
  | { readonly kind: 'unreachable' };

export function TenantCompanyOnboardingScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [entityKind, setEntityKind] = useState('company_ltd');
  const [taxId, setTaxId] = useState('');
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('company.create')} requires="TENANT" actual={ctx.surface} />;
  }

  /** The server's refusal, if the office is already at its purchased limit. */
  const refusal = fixturesEnabled() ? fixtureCompanyCapacityRefusal : null;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setOutcome(null);
    try {
      const created = await callOperation('companies.company.create', {
        body: {
          legalName,
          displayName,
          entityKind,
          taxId: taxId.trim() === '' ? null : taxId.trim(),
        },
      });
      setOutcome({ kind: 'created', companyUid: created.companyUid });
    } catch (err: unknown) {
      if (err instanceof DeniedError) {
        setOutcome({ kind: 'denied', body: err.body });
      } else if (err instanceof ConflictError) {
        /*
         * A REFUSAL WITH A REASON, IN HEBREW. **2026-10-20.**
         *
         * There was no branch for a 409 at all, so every named refusal this path produces — an
         * absent official number, a failed check digit, a malformed one, a full office — fell into
         * `error` and was shown as a support reference. The server knows which of five it was.
         */
        setOutcome({ kind: 'refused', body: err.body, requestUid: err.requestUid });
      } else if (err instanceof ApiError && err.status === 0) {
        setOutcome({ kind: 'unreachable' });
      } else if (err instanceof ApiError) {
        setOutcome({ kind: 'error', requestUid: err.requestUid });
      } else {
        setOutcome({ kind: 'unreachable' });
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('company.create')}
      backToParent={{ labelHe: t('settings.e.companies'), href: routes.tenantCompanies }}
    >
      {refusal === null ? null : <CapacityRefusalNotice refusal={refusal} />}

      {/*
        ── WHERE AM I, AND IS THIS ALL OF IT? **§3, 2026-10-25.** ────────────────────────────────

        Owner: *"שאני נכנס להקמת חברה אני לא יודע באיזה שלב אני נמצא ואם אלו רק ההגדרות להקמת חברה
        לדעתי חסר לנו עוד מידע אלא אם זה במסך הבא אחרי הקמת החברה."* Both halves of that are a fair
        reading of what the screen showed: a title, a form, and nothing about what it is part of.

        THE THREE STEPS ARE NOT INVENTED. They are what this flow already does, read off the code:

          1. this form — the four identity fields the server requires;
          2. the company is created `prospect`, which `provenanceHe` below has always said;
          3. `הענקת גישה לחברה` — the link the success state already offers, because a new company
             is reachable by nobody until somebody is granted access to it.

        It is an `<ol>` with `data-state`, reusing `.steps` from `MfaEnrolmentScreen` rather than
        inventing a second progress component. The current step is marked by `aria-current`, by its
        weight and by its border — never by colour alone.

        HORIZONTAL, NOT `steps--stacked`. Stacked, each pill stretches to the full content width —
        three empty bars across a 1150px screen, which is more of the empty space §3 asks to remove.
        `.steps` wraps, so at a narrow width the three fall onto their own lines anyway.
      */}
      <ol className="steps">
        <li className="steps__item" data-state="current" aria-current="step">
          <span className="steps__index" dir="ltr">
            1
          </span>
          <span className="steps__label">{t('onboard.whatStep1')}</span>
        </li>
        <li className="steps__item" data-state="pending">
          <span className="steps__index" dir="ltr">
            2
          </span>
          <span className="steps__label">{t('onboard.whatStep2')}</span>
        </li>
        <li className="steps__item" data-state="pending">
          <span className="steps__index" dir="ltr">
            3
          </span>
          <span className="steps__label">{t('onboard.whatStep3')}</span>
        </li>
      </ol>

      <form className="form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">{t('onboard.legalName')}</span>
          <input
            className="field__input"
            value={legalName}
            onChange={(e) => setLegalName(e.currentTarget.value)}
            required
          />
        </label>

        <label className="field">
          <span className="field__label">{t('onboard.displayName')}</span>
          <input
            className="field__input"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            required
          />
        </label>

        <fieldset className="field field--choice">
          <legend className="field__label">{t('onboard.entityKind')}</legend>
          {ENTITY_KINDS.map((k) => (
            <label key={k.code} className="choice">
              <input
                type="radio"
                name="entityKind"
                value={k.code}
                checked={entityKind === k.code}
                onChange={() => setEntityKind(k.code)}
              />
              <span>{t(k.label)}</span>
            </label>
          ))}
          <span className="field__hint">{t('onboard.entityKindHint')}</span>
        </fieldset>

        {/*
          THE OFFICIAL NUMBER, AND IT IS REQUIRED. **The Owner's finding 6, 2026-10-20.**

          This said *"אפשר להשאיר ריק"*, and the company-management chapter (0.6 §4, §14) rules the
          other way: the official number is one of five mandatory fields, and *"אין מסלול ״חברת
          טיוטה ללא מספר רשמי״ כחלופה להשלמת ההגדרות"*. What the old hint promised was not true
          either — a company created with no number did not appear "marked for completion", it
          failed at the database, because the insert wrote a `tax_id_status` the CHECK constraint
          does not admit.

          The server verifies the number against the estate's own declared format and the Israeli
          check digit; this field only collects it and names which number it is.
        */}
        <label className="field">
          <span className="field__label">{officialIdLabelHe(entityKind)}</span>
          <input
            className="field__input"
            name="taxId"
            dir="ltr"
            inputMode="numeric"
            value={taxId}
            onChange={(e) => setTaxId(e.currentTarget.value)}
            required
          />
          <span className="field__hint">
            {t('onboard.numberHint')}
          </span>
        </label>

        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={pending}>
            {pending ? t('common.sending') : t('onboard.submit')}
          </button>
          <a className="btn" href={routes.tenantCompanies}>
            {t('common.cancel')}
          </a>
        </div>
      </form>

      {outcome === null ? null : outcome.kind === 'created' ? (
        <StateBlock
          titleHe={t('onboard.createdTitle')}
          bodyHe={t('onboard.createdBody')}
          nextStep={
            <a className="btn btn--nav" href={routes.tenantCompanyAccess}>
              {t('onboard.grantAccess')}
            </a>
          }
        />
      ) : outcome.kind === 'refused' ? (
        (() => {
          const text = companyRefusalText(outcome.body);
          return (
            <section className="denial" data-tone="warn" role="alert">
              <h2 className="denial__title">{text.titleHe}</h2>
              <p className="denial__explanation">{text.explanationHe}</p>
              <p className="denial__explanation">{text.unchangedHe}</p>
              <p className="denial__next-step">{text.nextStepHe}</p>
              <p className="denial__explanation">{`${t('common.supportRef')} ${outcome.requestUid}`}</p>
            </section>
          );
        })()
      ) : outcome.kind === 'denied' ? (
        <DenialNotice deny={outcome.body} />
      ) : outcome.kind === 'error' ? (
        /*
         * ── THE SCREEN THAT PRODUCED THE 207 SAYS SOMETHING USEFUL NOW. **2026-10-25.** ────────
         *
         * This rendered a bare `<ErrorState>`: *"אירעה תקלה / לא הצלחנו להשלים את הפעולה"* and a
         * code. That is exactly what the Owner saw when `createCompany` emitted a bare column in an
         * OUTPUT clause, and it told him nothing — not whether a company had been created, not
         * whether it was safe to press the button again.
         *
         * WHAT IS SAFE TO ASSERT, AND WHAT IS NOT. The service wraps the store call and the audit
         * row in ONE transaction, so a server-side fault during creation rolls back — that is
         * proven in `customer-admin-store.test.ts`. But this branch is reached for ANY answered
         * fault, and a fault raised after a commit would look identical from here. So the screen
         * does not promise that nothing was created; it tells the person to LOOK before retrying,
         * which is right either way and is the product's existing stance about repeating a write.
         */
        <ErrorState
          requestUid={outcome.requestUid}
          whatHappenedHe={t('onboard.failedBody')}
          whatToDoHe={t('onboard.failedNext')}
          action={
            <a className="btn btn--nav" href={routes.tenantCompanies}>
              {t('onboard.toCompanyList')}
            </a>
          }
        />
      ) : (
        <StateBlock
          titleHe={t('onboard.notCreatedTitle')}
          bodyHe={t('onboard.unreachableBody')}
          tone="error"
          nextStep={<p>{t('common.retryNothingSaved')}</p>}
        />
      )}
    </AppShell>
  );
}
